import { http, HttpResponse } from 'msw';
import type {
  AppliancePortControlResponse,
  EdgeApplianceResponse,
  FleetPageOfFleetRolloutEventResponse,
  FleetStatisticsResponse,
  MaintenanceCaseResponse,
  SiteCommissioningAuditResponse,
  SiteComplianceStatusResponse,
  SiteNetworkSegmentResponse,
  SiteOperationalModeResponse,
  SiteReadinessCheckResponse,
  SiteScheduleResponse,
} from '../generated/models';
import type { NvrAdmissionsResponse, NvrProbeDetails } from '../generated/fleet/models';
import type { EdgeApplianceStockSummary } from '../edge-appliance-inventory';
import type {
  EdgeApplianceTwin,
  EdgeApplianceTwinSummary,
  EdgeTwinConnectivityState,
  EdgeTwinFacetMetadata,
  EdgeTwinHealthReason,
  EdgeTwinHealthState,
  EdgeTwinLifecycleState,
} from '../edge-appliance-twins';
import {
  CENTRAL_SSP_IDS,
  CENTRAL_SSU_IDS,
  DAY_MS,
  HOUR_MS,
  downtownSiteId,
  iso,
  marinaSiteId,
  mockDigest,
  mockUserId,
  serviceYardSiteId,
  siteNvrId,
} from './fixture-ids';

// Fleet appliances and site operations: one appliance set backs the inventory and fleet lists,
// the twins, observability, port control and the per-site commissioning record.

type Connectivity = Pick<EdgeApplianceResponse['connectivity'], 'status' | 'reason'> & {
  status: EdgeTwinConnectivityState;
};

interface Box {
  n: number;
  hardwareId: string;
  siteId: string | null;
  sspId: string;
  ssuId: string | null;
  lifecycle: EdgeTwinLifecycleState;
  status: string;
  connectivity: Connectivity;
  health: EdgeTwinHealthState;
  healthReasons: EdgeTwinHealthReason[];
  cohort: 'Canary' | 'Early' | 'Stable' | 'Lagging';
  release: string;
  createdDaysAgo: number;
}

const acme = CENTRAL_SSP_IDS['ssp-acme'];
const sspAdminUserId = mockUserId(3);
const sspTechUserId = mockUserId(4);

const boxes: Box[] = [
  {
    n: 42,
    hardwareId: 'CG-EDGE-0042',
    siteId: marinaSiteId,
    sspId: acme,
    ssuId: CENTRAL_SSU_IDS['ssu-marina'],
    lifecycle: 'Live',
    status: 'Live',
    connectivity: { status: 'Online', reason: 'Healthy' },
    health: 'Healthy',
    healthReasons: [],
    cohort: 'Stable',
    release: '2026.10.1',
    createdDaysAgo: 120,
  },
  {
    n: 57,
    hardwareId: 'CG-EDGE-0057',
    siteId: downtownSiteId,
    sspId: acme,
    ssuId: CENTRAL_SSU_IDS['ssu-downtown'],
    lifecycle: 'Live',
    status: 'Live',
    connectivity: { status: 'Degraded', reason: 'IpsecDown' },
    health: 'Attention',
    healthReasons: ['IpsecDown'],
    cohort: 'Early',
    release: '2026.10.1',
    createdDaysAgo: 96,
  },
  {
    n: 63,
    hardwareId: 'CG-EDGE-0063',
    siteId: serviceYardSiteId,
    sspId: CENTRAL_SSP_IDS['ssp-secure'],
    ssuId: CENTRAL_SSU_IDS['ssu-yard'],
    lifecycle: 'Maintenance',
    status: 'Maintenance',
    connectivity: { status: 'Offline', reason: 'HeartbeatExpired' },
    health: 'Critical',
    healthReasons: ['VitalsUnavailable', 'IpsecObservationStale'],
    cohort: 'Lagging',
    release: '2026.09.3',
    createdDaysAgo: 64,
  },
  {
    n: 107,
    hardwareId: 'CG-EDGE-0107',
    siteId: null,
    sspId: acme,
    ssuId: null,
    lifecycle: 'Allocated',
    status: 'Unbound',
    connectivity: { status: 'NeverSeen', reason: 'HeartbeatNeverReceived' },
    health: 'Attention',
    healthReasons: ['VitalsUnavailable'],
    cohort: 'Stable',
    release: '2026.10.1',
    createdDaysAgo: 21,
  },
  {
    n: 108,
    hardwareId: 'CG-EDGE-0108',
    siteId: null,
    sspId: acme,
    ssuId: null,
    lifecycle: 'Allocated',
    status: 'Unbound',
    connectivity: { status: 'NeverSeen', reason: 'HeartbeatNeverReceived' },
    health: 'Attention',
    healthReasons: ['VitalsUnavailable'],
    cohort: 'Canary',
    release: '2026.10.1',
    createdDaysAgo: 4,
  },
];

export const siteEdgeOnline = (siteId: string): boolean =>
  boxes.some((box) => box.siteId === siteId && box.connectivity.status === 'Online');

const pad = (n: number, width: number) => String(n).padStart(width, '0');
const applianceId = (b: Box) => `50000000-0000-4000-8000-${pad(b.n, 12)}`;
const portMac = (b: Box, port: number) =>
  `02:43:47:00:${pad(Math.floor(b.n / 100), 2)}:${pad(b.n % 100, 2)}`.replace(/..$/, (last) =>
    port === 1 ? last : `a${port}`,
  );
const seen = (b: Box) => b.connectivity.status !== 'NeverSeen';
const sited = (b: Box) => b.siteId !== null;
const heartbeatAgeSeconds = (b: Box) => (b.connectivity.status === 'Offline' ? 5_400 : 12);
const ago = (seconds: number) => iso(-seconds * 1000);
const runningRelease = (b: Box) => (b.connectivity.status === 'Online' ? b.release : '2026.09.2');
// The Service Yard box is still in commissioning: alarm core confirmed, video awaiting review.
const videoPending = (b: Box) => b.siteId === serviceYardSiteId;

const recorded = (ageSeconds: number): EdgeTwinFacetMetadata => ({
  enforcement: 'Recorded',
  source: 'edge-heartbeat',
  observedAt: ago(ageSeconds),
  ageSeconds,
});
const unavailable: EdgeTwinFacetMetadata = {
  enforcement: 'Enforced',
  source: 'edge-heartbeat',
  observedAt: null,
  ageSeconds: null,
};

function twin(b: Box): EdgeApplianceTwin {
  const age = heartbeatAgeSeconds(b);
  const facet = sited(b) ? recorded(age) : unavailable;
  const online = b.connectivity.status === 'Online';
  return {
    applianceId: applianceId(b),
    composedAt: ago(2),
    identity: {
      hardwareId: b.hardwareId,
      macAddress: portMac(b, 1),
      formFactor: 'Commercial',
      identityAnchor: 'Tpm',
      lifecycleState: b.lifecycle,
      custodianSspId: b.sspId,
      siteId: b.siteId,
      sspId: b.sspId,
      ssuId: b.ssuId,
      assuranceProfile: 'BaselineAssurance',
      rootOfTrustTier: 'H',
      credentialOnlyAssurance: false,
      metadata: seen(b) ? recorded(age) : unavailable,
    },
    connectivity: {
      ...b.connectivity,
      lastSeen: seen(b) ? ago(age) : null,
      ageSeconds: seen(b) ? age : null,
      nextIntervalSeconds: 15,
      offlineThresholdSeconds: 60,
      metadata: seen(b) ? recorded(age) : unavailable,
    },
    desired: {
      scope: 'fleet-wide',
      release: `commercial-${b.release}`,
      channel: 'commercial',
      cohort: b.cohort.toLowerCase(),
      pin: null,
      metadata: { ...unavailable, enforcement: 'Declared' },
    },
    reported: sited(b)
      ? {
          availability: 'Available',
          release: `commercial-${runningRelease(b)}`,
          syncHealth: online ? 'Synced' : 'OutOfSync',
          metadata: facet,
        }
      : { availability: 'Unknown', release: null, syncHealth: null, metadata: unavailable },
    host: sited(b)
      ? {
          cpuUsagePercent: online ? 23 : 61,
          memoryUsagePercent: 48,
          rootDiskUsagePercent: 41,
          cpuTemperatureCelsius: 52,
          uptimeSeconds: 1_296_000,
          metadata: facet,
        }
      : {
          cpuUsagePercent: null,
          memoryUsagePercent: null,
          rootDiskUsagePercent: null,
          cpuTemperatureCelsius: null,
          uptimeSeconds: null,
          metadata: unavailable,
        },
    ipsec: {
      online: sited(b) ? online : false,
      ikeSaUp: sited(b) ? online : false,
      childSaUp: sited(b) ? online : false,
      xfrmInterfaceUp: sited(b),
      protectedRoutesInstalled: sited(b),
      trafficClassRulesInstalled: sited(b),
      reportedGeneration: sited(b) ? 4 : null,
      reportedConfigurationSha256: sited(b) ? mockDigest('a') : null,
      metadata: facet,
    },
    ports: {
      desiredGeneration: 4,
      desiredSha256: mockDigest('b'),
      appliedGeneration: 4,
      appliedSha256: mockDigest('b'),
      reportedGeneration: 4,
      reportedSha256: mockDigest('b'),
      lastErrorCode: null,
      items: sited(b)
        ? [1, 2].map((port) => ({
            portId: `lan${port}`,
            permanentMac: portMac(b, port),
            observed: true,
            linkUp: true,
            configured: true,
          }))
        : [],
      metadata: facet,
    },
    commissioning: sited(b)
      ? {
          state: videoPending(b) ? 'AlarmCorePromoted' : 'FullyPromoted',
          alarmCorePromotion: 'Confirmed',
          videoPromotion: videoPending(b) ? 'Submitted' : 'Confirmed',
          hasQuarantinedRecorder: false,
          metadata: facet,
        }
      : {
          state: 'NotApplicable',
          alarmCorePromotion: null,
          videoPromotion: null,
          hasQuarantinedRecorder: false,
          metadata: unavailable,
        },
    convergence: sited(b)
      ? { status: online ? 'UpToDate' : 'OutOfDate', metadata: facet }
      : { status: 'Unknown', metadata: unavailable },
    health: { status: b.health, reasons: b.healthReasons, metadata: recorded(age) },
    alerts: {
      availability: 'Available',
      firing:
        b.health === 'Critical'
          ? [
              {
                name: 'EdgeHeartbeatMissing',
                severity: 'critical',
                component: 'edge-agent',
                activeAtUtc: ago(5_000),
              },
            ]
          : [],
      metadata: recorded(30),
    },
  };
}

const ociBundle = (name: string, version: string) => ({
  name,
  version,
  ociRef: `harbor.cguard.local/cguard/${name}:${version}`,
});

/** EdgeApplianceResponse; the hardware model fields also serve the inventory list. */
function appliance(b: Box): EdgeApplianceResponse {
  const heartbeat = seen(b) ? ago(heartbeatAgeSeconds(b)) : null;
  const vitals = seen(b) && b.connectivity.status !== 'Offline';
  return {
    applianceId: applianceId(b),
    hardwareId: b.hardwareId,
    macAddress: portMac(b, 1),
    formFactor: 'Commercial',
    assignedSiteId: b.siteId,
    status: b.status,
    cohort: b.cohort,
    kairosImage: ociBundle('cguard-kairos', b.release),
    k3s: ociBundle('k3s', '1.36.1'),
    aiPipeline: ociBundle('cguard-ai', b.release),
    configBundle: ociBundle('site-config', '42'),
    createdAtUtc: iso(-b.createdDaysAgo * DAY_MS),
    pairedAtUtc: sited(b) ? iso(-(b.createdDaysAgo - 2) * DAY_MS) : null,
    connectivity: { ...b.connectivity, nextIntervalSeconds: 15, offlineThresholdSeconds: 60 },
    lastHeartbeatUtc: heartbeat,
    ipsecProbeReportedAtUtc: heartbeat,
    ipsecOnline: heartbeat === null ? null : b.connectivity.status === 'Online',
    decommissionedAtUtc: null,
    cpuUsagePercent: vitals ? 23 : null,
    ramUsagePercent: vitals ? 48 : null,
    storageUsagePercent: vitals ? 41 : null,
    uptimeSeconds: vitals ? 1_296_000 : null,
    vitalsReportedAt: vitals ? heartbeat : null,
    ekDigest: mockDigest(pad(b.n % 100, 2)),
    identityAnchor: 'Tpm',
    credentialOnlyAssurance: false,
    observedSerialAtBirth: null,
    latestRelease: null,
    hardwareModel: 'ASUS NUC 14 Pro (125H)',
    hardwareModelProvenance: 'Attested',
    hardwareIdProvenance: 'FactoryDeclared',
    physicalPorts: [1, 2, 3, 4].map((port) => ({
      portId: `lan${port}`,
      permanentMac: portMac(b, port),
      pciAddress: `0000:0${port}:00.0`,
      maxSpeedMbps: port <= 2 ? 1_000 : 2_500,
      isBootstrap: port === 1,
    })),
  };
}

function observability(b: Box) {
  const take = 30;
  const base = { applianceId: applianceId(b), take, freshnessThresholdSeconds: 90 };
  if (!seen(b)) return { ...base, latest: null, history: [] };
  const offline = b.connectivity.status === 'Offline';
  const history = Array.from({ length: 18 }, (_, i) => {
    const age = heartbeatAgeSeconds(b) + i * 15;
    if (offline) {
      return {
        receivedAtUtc: ago(age),
        agentVersion: b.release,
        freshness: 'stale',
        hostStatusAvailable: false,
        hostStatusUnavailableReason: 'stale_heartbeat',
        hostStatus: null,
      };
    }
    return {
      receivedAtUtc: ago(age),
      agentVersion: b.release,
      freshness: 'fresh',
      hostStatusAvailable: true,
      hostStatusUnavailableReason: null,
      hostStatus: {
        schema_version: 8,
        observed_at_utc: ago(age + 1),
        vitals: {
          host_uptime_seconds: 1_296_000 - i * 15,
          cpu_utilization_percent: 18 + ((i * 7) % 15),
          memory_utilization_percent: 46 + ((i * 3) % 6),
          root_disk_utilization_percent: 41,
        },
        cluster: { workloads_ready: 14, workloads_total: 14 },
        observability: {
          certificate_seconds_remaining: 2_419_200,
          queue_disk_usage_bytes: 4_194_304,
        },
      },
    };
  });
  return { ...base, latest: history[0], history };
}

function portControl(b: Box): AppliancePortControlResponse {
  return {
    applianceId: applianceId(b),
    desiredGeneration: 6,
    desiredSha256: mockDigest('7'),
    appliedGeneration: 6,
    appliedSha256: mockDigest('7'),
    status: 'applied',
    errorCode: null,
    reportedAtUtc: ago(40),
    allowedModes: ['unused', 'camera-switch', 'alarm', 'camera+alarm'],
    ports: [
      {
        portId: 'lan1',
        protection: 'internet',
        mutable: false,
        desiredMode: 'internet',
        observed: true,
        linkUp: true,
      },
      {
        portId: 'lan2',
        protection: 'site-managed',
        mutable: true,
        desiredMode: 'camera-switch',
        observed: true,
        linkUp: true,
      },
      {
        portId: 'lan3',
        protection: 'site-managed',
        mutable: true,
        desiredMode: 'alarm',
        observed: true,
        linkUp: true,
      },
      {
        portId: 'lan4',
        protection: 'site-managed',
        mutable: true,
        desiredMode: 'unused',
        observed: false,
        linkUp: null,
      },
    ],
  };
}

function countBy<T extends string | null>(key: (b: Box) => T): Array<[T, number]> {
  const counts = new Map<T, number>();
  for (const b of boxes) counts.set(key(b), (counts.get(key(b)) ?? 0) + 1);
  return [...counts];
}

function twinSummary(): EdgeApplianceTwinSummary {
  return {
    total: boxes.length,
    byLifecycleState: countBy((b) => b.lifecycle).map(([state, count]) => ({ state, count })),
    byCohort: countBy((b) => b.cohort).map(([cohort, count]) => ({ cohort, count })),
    cohortLifecycleMatrix: countBy((b) => `${b.cohort}|${b.lifecycle}`).map(([key, count]) => {
      const index = key.indexOf('|');
      return {
        cohort: key.slice(0, index),
        state: key.slice(index + 1) as EdgeTwinLifecycleState,
        count,
      };
    }),
    pinned: 0,
    connectivity: {
      online: boxes.filter((b) => b.connectivity.status === 'Online').length,
      offline: boxes.filter((b) => b.connectivity.status === 'Offline').length,
    },
  };
}

function stockSummary(): EdgeApplianceStockSummary {
  return {
    asOfUtc: ago(30),
    total: boxes.length,
    countsByStatus: countBy((b) => b.status).map(([status, count]) => ({
      status: status as EdgeApplianceStockSummary['countsByStatus'][number]['status'],
      count,
    })),
    countsByCustodian: countBy((b) => b.sspId).map(([custodianSspId, count]) => ({
      custodianSspId,
      count,
    })),
    aging: {
      lifecycleStatus: 'Unbound',
      timestampField: 'createdAtUtc',
      buckets: [
        { key: '0-7', minimumAgeDaysInclusive: 0, maximumAgeDaysExclusive: 7, count: 1 },
        { key: '7-30', minimumAgeDaysInclusive: 7, maximumAgeDaysExclusive: 30, count: 1 },
        { key: '30-90', minimumAgeDaysInclusive: 30, maximumAgeDaysExclusive: 90, count: 0 },
        { key: '90+', minimumAgeDaysInclusive: 90, maximumAgeDaysExclusive: null, count: 0 },
      ],
    },
  };
}

function rolloutStatistics(): FleetStatisticsResponse {
  const counts = (dimension: string, key: (b: Box) => string) =>
    countBy(key).map(([value, count]) => ({ dimension, value, count }));
  return {
    observedAtUtc: ago(20),
    counts: [
      ...counts('ring', (b) => b.cohort),
      ...counts('lifecycle', (b) => b.lifecycle),
      ...counts('connectivity', (b) => b.connectivity.status),
      ...counts('drift', (b) => twin(b).convergence.status),
      ...boxes
        .filter(sited)
        .map((b) => `commercial-${runningRelease(b)}`)
        .reduce<Array<{ dimension: string; value: string; count: number }>>((rows, value) => {
          const row = rows.find((r) => r.value === value);
          if (row) row.count += 1;
          else rows.push({ dimension: 'runningOsRelease', value, count: 1 });
          return rows;
        }, []),
    ],
  };
}

function rolloutEvents(siteId: string | null): FleetPageOfFleetRolloutEventResponse {
  const rolloutId = '6b7c8d9e-0000-4000-8000-000000000401';
  return {
    items: [
      {
        id: '6b7c8d9e-0000-4000-8000-000000000411',
        rolloutId,
        siteId,
        actorId: null,
        transition: 'Completed',
        reason: 'Appliance reported commercial-2026.10.1 healthy.',
        occurredAtUtc: iso(-26 * HOUR_MS),
      },
      {
        id: '6b7c8d9e-0000-4000-8000-000000000412',
        rolloutId,
        siteId,
        actorId: null,
        transition: 'Started',
        reason: 'Wave 2 (Stable cohort) began.',
        occurredAtUtc: iso(-27 * HOUR_MS),
      },
      {
        id: '6b7c8d9e-0000-4000-8000-000000000413',
        rolloutId,
        siteId: null,
        actorId: sspTechUserId,
        transition: 'Approved',
        reason: 'Release commercial-2026.10.1 approved for the Stable cohort.',
        occurredAtUtc: iso(-30 * HOUR_MS),
      },
    ],
    next: null,
  };
}

function networkSegments(b: Box): SiteNetworkSegmentResponse[] {
  const segment = {
    gateway: null,
    segmentAddressingRole: null,
    customerUplinkSubnetCidr: null,
    dhcpServing: null,
  };
  return [
    {
      ...segment,
      segmentId: 'underlay',
      purposes: ['internet'],
      portId: 'lan1',
      permanentMac: portMac(b, 1),
      vlanId: null,
      addressingMode: 'customer-dhcp',
      addressCidr: null,
      dnsServers: [],
      isDefaultRoute: true,
      ownership: 'site-managed',
      roles: ['uplink'],
      connectionId: 'cguard-site-lan1',
      connectionUuid: '1bf7f763-436d-48eb-8717-d314beb36f4a',
      profileSha256: mockDigest('c'),
      catalogEntrySha256: mockDigest('d'),
      customerDhcp: { acceptRoutes: true, acceptDns: false },
    },
    {
      ...segment,
      segmentId: 'camera-lan',
      purposes: ['management', 'camera'],
      portId: 'lan2',
      permanentMac: portMac(b, 2),
      vlanId: 30,
      addressingMode: 'static',
      addressCidr: '10.30.0.1/24',
      dnsServers: ['10.30.0.53', '10.30.0.54'],
      isDefaultRoute: false,
      ownership: 'cguard-owned',
      roles: [],
      connectionId: null,
      connectionUuid: null,
      profileSha256: null,
      catalogEntrySha256: null,
      customerDhcp: null,
    },
  ];
}

function commissioning(siteId: string): SiteCommissioningAuditResponse | null {
  const b = boxes.find((box) => box.siteId === siteId);
  if (!b) return null;
  const submitted = {
    state: 'Submitted',
    submittedByUserId: sspTechUserId,
    submittedAtUtc: iso(-(b.createdDaysAgo - 3) * DAY_MS),
    confirmedByUserId: null,
    confirmedAtUtc: null,
    rejectionReason: null,
    signalsCurrentlyMet: true,
  };
  const confirmed = {
    ...submitted,
    state: 'Confirmed',
    confirmedByUserId: sspAdminUserId,
    confirmedAtUtc: iso(-(b.createdDaysAgo - 4) * DAY_MS),
  };
  return {
    auditId: `8d9e0f1a-0000-4000-8000-${pad(b.n, 12)}`,
    applianceId: applianceId(b),
    siteId,
    overallStatus: videoPending(b) ? 'AlarmCorePromoted' : 'FullyPromoted',
    expectedBoxMac: portMac(b, 1),
    panelDeclared: true,
    declaredNvrCount: 1,
    declaredCameraCount: 6,
    criticalCameraCount: 2,
    createdAtUtc: iso(-(b.createdDaysAgo - 2) * DAY_MS),
    latestEvidenceAtUtc: iso(-(b.createdDaysAgo - 3) * DAY_MS),
    latestVerdict: null,
    alarmCore: confirmed,
    video: videoPending(b) ? submitted : confirmed,
    alarmCoreStabilityWindowMet: true,
    networkPlan: {
      schemaVersion: 2,
      bindingGeneration: 'A'.repeat(43),
      intentSha256: mockDigest('e'),
      segments: networkSegments(b),
    },
    fieldTransport: {
      bindingState: 'Bound',
      lastCentralContactAtUtc: ago(heartbeatAgeSeconds(b)),
      ipsecDeliveryState: 'configuration_applied',
      ipsecConfigurationAppliedAtUtc: iso(-3 * DAY_MS),
    },
    networkCommissioningBootstrap: null,
    recorderQuarantines: [],
    cameraProvisioning: null,
    rosterAmendments: [],
    rosterAmendmentProposals: [],
    cameraRosterFindings: [],
  };
}

function readiness(siteId: string): SiteComplianceStatusResponse {
  const ok = siteId !== serviceYardSiteId;
  const evaluatedAtUtc = iso(-2 * DAY_MS);
  const check = (
    n: number,
    family: string,
    code: string,
    pass: boolean,
    reason: string | null,
  ): SiteReadinessCheckResponse => ({
    checkId: `0c1d2e3f-0000-4000-8000-${pad(n, 12)}`,
    family,
    code,
    required: true,
    status: pass ? 'pass' : 'fail',
    expectedJson: '{}',
    actualJson: '{}',
    evaluatedAtUtc,
    failureReason: pass ? null : reason,
  });
  return {
    siteId,
    siteStatus: ok ? 'Live' : 'Provisioning',
    scenarioProfileKey: 'commercial-high-rise',
    goLiveBasis: null,
    wentLiveAtUtc: null,
    currentComplianceExceptionId: null,
    currentComplianceFollowUpCaseId: null,
    complianceFollowUpDueAtUtc: null,
    latestReadinessRun: {
      runId: '7b0c1d2e-3f40-4a51-8b62-0000000000c1',
      siteId,
      scenarioProfileKey: 'commercial-high-rise',
      submittedByUserId: sspAdminUserId,
      submittedAtUtc: iso(-2 * DAY_MS - 10 * 60_000),
      completedAtUtc: evaluatedAtUtc,
      status: ok ? 'Passed' : 'Failed',
      certificationPermitted: ok,
      isCertifiable: ok,
      evidenceSnapshotSha256: mockDigest('f'),
      checks: [
        check(1, 'technical', 'edge-commissioning', ok, 'Video promotion is awaiting review.'),
        check(2, 'business', 'customer-relationship-authority', true, null),
        check(3, 'material', 'installed-base-corroboration', true, null),
      ],
    },
    currentCertificate: null,
  };
}

function maintenanceCases(siteId: string): MaintenanceCaseResponse[] {
  return [
    {
      maintenanceCaseId: '3e4f5a6b-0000-4000-8000-000000000101',
      siteId,
      reasonCode: 'CameraReplacement',
      reason: 'Replace the lobby dome camera (CAM-07) after water ingress.',
      startsAtUtc: iso(DAY_MS),
      endsAtUtc: iso(DAY_MS + 4 * HOUR_MS),
      status: 'PendingApproval',
      requestedByUserId: sspTechUserId,
      requestedAtUtc: iso(-3 * HOUR_MS),
      approvedByUserId: null,
      approvedAtUtc: null,
      operationalModeId: null,
      closureReason: null,
      closureNotes: null,
      closedByUserId: null,
      closedAtUtc: null,
    },
    {
      maintenanceCaseId: '3e4f5a6b-0000-4000-8000-000000000102',
      siteId,
      reasonCode: 'FirmwareUpgrade',
      reason: 'NVR firmware upgrade during the low-occupancy window.',
      startsAtUtc: iso(-7 * DAY_MS - 2 * HOUR_MS),
      endsAtUtc: iso(-7 * DAY_MS),
      status: 'Closed',
      requestedByUserId: sspAdminUserId,
      requestedAtUtc: iso(-9 * DAY_MS),
      approvedByUserId: sspTechUserId,
      approvedAtUtc: iso(-8 * DAY_MS),
      operationalModeId: '4f5a6b7c-0000-4000-8000-000000000201',
      closureReason: 'RoutineCompleted',
      closureNotes: 'All 18 cameras re-registered and streaming.',
      closedByUserId: sspAdminUserId,
      closedAtUtc: iso(-7 * DAY_MS),
    },
  ];
}

function operationalModes(siteId: string): SiteOperationalModeResponse[] {
  return [
    {
      operationalModeId: '4f5a6b7c-0000-4000-8000-000000000202',
      siteId,
      kind: 'Verification',
      reasonCode: 'PostIncidentReview',
      reason: 'Verify perimeter analytics after the façade works.',
      startsAtUtc: iso(-2 * HOUR_MS),
      endsAtUtc: iso(22 * HOUR_MS),
      createdByUserId: sspAdminUserId,
      createdAtUtc: iso(-2 * HOUR_MS - 5 * 60_000),
      maintenanceCaseId: null,
      endedByUserId: null,
      endedAtUtc: null,
      endReason: null,
      suppressionPolicies: [],
    },
  ];
}

const WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function schedule(siteId: string, kind: string): SiteScheduleResponse {
  const arming = kind === 'ArmingSchedule';
  return {
    scheduleId: `5a6b7c8d-0000-4000-8000-00000000030${arming ? 1 : 2}`,
    siteId,
    kind,
    timeZoneId: 'Asia/Dubai',
    updatedByUserId: sspAdminUserId,
    updatedAtUtc: iso(-14 * DAY_MS),
    days: WEEK.map((dayOfWeek) => {
      const weekend = dayOfWeek === 'Saturday' || dayOfWeek === 'Sunday';
      if (arming) {
        return {
          dayOfWeek,
          enabled: true,
          startsAtLocal: weekend ? '00:00:00' : '19:00:00',
          endsAtLocal: '23:59:00',
        };
      }
      return {
        dayOfWeek,
        enabled: !weekend,
        startsAtLocal: weekend ? null : '08:00:00',
        endsAtLocal: weekend ? null : '18:00:00',
      };
    }),
    exceptions: [
      {
        localDate: '2026-12-02',
        enabled: false,
        startsAtLocal: null,
        endsAtLocal: null,
        reason: 'National Day',
      },
    ],
  };
}

function paged<T>(items: T[], url: URL) {
  const page = Math.max(1, Number(url.searchParams.get('page') ?? 1) || 1);
  const pageSize = Math.max(1, Number(url.searchParams.get('pageSize') ?? 50) || 50);
  return {
    items: items.slice((page - 1) * pageSize, page * pageSize),
    page,
    pageSize,
    total: items.length,
    totalPages: Math.ceil(items.length / pageSize),
  };
}

const notFound = () => HttpResponse.json({ title: 'Not found', status: 404 }, { status: 404 });
const boxById = (id: unknown) => boxes.find((b) => applianceId(b) === id);

/** The admin overview `fleet` counters, bucketed by appliance status as Central does. */
export function fleetOverviewCounters() {
  const count = (...statuses: string[]) => boxes.filter((b) => statuses.includes(b.status)).length;
  return {
    total: boxes.length,
    online: count('Live'),
    offline: count('Bound', 'Maintenance'),
    provisioning: count('Factory', 'Unbound'),
  };
}

// Specific paths come before `/v1/edge/appliances/:id`, which would otherwise match them.
export const fleetSiteHandlers = [
  http.get('/v1/fleet/appliances/:id/nvr-admissions', ({ params }) => {
    const box = boxById(params.id);
    if (!box) return notFound();
    return HttpResponse.json({
      applianceId: applianceId(box), bindingGeneration: '1', intentSha256: mockDigest('ab'),
      revision: 1, appliedRevision: 1, mode: 0,
      entries: box.siteId ? [{
        admissionId: siteNvrId(box.siteId), segmentId: 'camera-lan', portId: 'lan1',
        macAddress: portMac(box, 1), ipAddress: '192.168.50.10',
        lastObservedIpAddress: '192.168.50.10', probeStatus: 'Succeeded',
      }] : [],
    } satisfies NvrAdmissionsResponse);
  }),
  http.get('/v1/fleet/appliances/:id/nvr-admissions/:admissionId/probe', ({ params }) => {
    const box = boxById(params.id);
    if (!box?.siteId || params.admissionId !== siteNvrId(box.siteId)) return notFound();
    return HttpResponse.json({
      version: 1, requestId: null, actorId: mockUserId(1), status: 'Succeeded',
      completedAtUtc: ago(60), result: {
        sessionId: siteNvrId(box.siteId), method: 'onvif',
        device: { vendor: 'Demo recorder', model: 'NVR', address: '192.168.50.10' },
        cameras: [], expiresAt: iso(HOUR_MS),
      },
    } satisfies NvrProbeDetails);
  }),
  http.get('/v1/edge/appliances/twins/summary', () => HttpResponse.json(twinSummary())),
  http.get('/v1/edge/appliances/twins', ({ request }) => {
    const url = new URL(request.url);
    const filter = (value: string | null, field: (b: Box) => string | null) => (b: Box) =>
      value === null || field(b) === value;
    const rows = boxes
      .filter(filter(url.searchParams.get('state'), (b) => b.lifecycle))
      .filter(filter(url.searchParams.get('siteId'), (b) => b.siteId))
      .filter(filter(url.searchParams.get('connectivity'), (b) => b.connectivity.status))
      .filter(filter(url.searchParams.get('health'), (b) => b.health));
    return HttpResponse.json(paged(rows.map(twin), url));
  }),
  http.get('/v1/edge/appliances/stock-summary', () => HttpResponse.json(stockSummary())),
  // Inventory (`/v1/edge/appliances?…`) and fleet (`/v1/edge/appliances/?…`) share one handler.
  http.get('/v1/edge/appliances', ({ request }) => {
    const url = new URL(request.url);
    const statuses = url.searchParams.getAll('status');
    const siteId = url.searchParams.get('siteId');
    const cohort = url.searchParams.get('cohort');
    const custodian = url.searchParams.get('custodianSspId');
    const q = url.searchParams.get('q')?.toLowerCase();
    const rows = boxes.filter(
      (b) =>
        (statuses.length === 0 || statuses.includes(b.status)) &&
        (siteId === null || b.siteId === siteId) &&
        (cohort === null || b.cohort === cohort) &&
        (custodian === null || b.sspId === custodian) &&
        (!q || b.hardwareId.toLowerCase().includes(q)),
    );
    return HttpResponse.json(paged(rows.map(appliance), url));
  }),
  http.post('/v1/edge/appliances/:id/cohort', async ({ params, request }) => {
    const b = boxById(params.id);
    if (!b) return notFound();
    b.cohort = ((await request.json()) as { cohort: Box['cohort'] }).cohort;
    return new HttpResponse(null, { status: 204 });
  }),
  http.get('/v1/edge/appliances/:id/twin', ({ params }) => {
    const b = boxById(params.id);
    return b ? HttpResponse.json(twin(b)) : notFound();
  }),
  http.get('/v1/edge/appliances/:id/observability', ({ params }) => {
    const b = boxById(params.id);
    return b ? HttpResponse.json(observability(b)) : notFound();
  }),
  http.get('/v1/edge/appliances/:id/ports', ({ params }) => {
    const b = boxById(params.id);
    return b ? HttpResponse.json(portControl(b)) : notFound();
  }),
  http.get('/v1/edge/appliances/:id', ({ params }) => {
    const b = boxById(params.id);
    return b ? HttpResponse.json(appliance(b)) : notFound();
  }),
  http.get('/v1/edge/commissioning/:siteId', ({ params }) => {
    const record = commissioning(String(params.siteId));
    return record ? HttpResponse.json(record) : notFound();
  }),
  http.get('/v1/edge/rollouts/statistics', () => HttpResponse.json(rolloutStatistics())),
  http.get('/v1/edge/rollouts/events', ({ request }) =>
    HttpResponse.json(rolloutEvents(new URL(request.url).searchParams.get('siteId'))),
  ),
  http.get('/v1/fleet/sites/:siteId/site-assignments', () => HttpResponse.json([])),
  http.get('/v1/sites/:id/readiness', ({ params }) =>
    HttpResponse.json(readiness(String(params.id))),
  ),
  http.get('/v1/sites/:id/maintenance-cases', ({ params }) =>
    HttpResponse.json(maintenanceCases(String(params.id))),
  ),
  http.get('/v1/sites/:id/operational-modes', ({ params }) =>
    HttpResponse.json(operationalModes(String(params.id))),
  ),
  http.get('/v1/sites/:id/schedules/:kind/evaluation', ({ params }) =>
    HttpResponse.json({
      siteId: String(params.id),
      kind: String(params.kind),
      evaluatedAtUtc: ago(5),
      active: params.kind === 'BusinessHours',
    }),
  ),
  http.get('/v1/sites/:id/schedules/:kind', ({ params }) =>
    HttpResponse.json(schedule(String(params.id), String(params.kind))),
  ),
];
